import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { Bot, CircleDot, Layers, Shield, Table2, Zap } from "lucide-react";

import { Alert } from "../../components/Alert/Alert";
import { AlertStack } from "../../components/Alert/AlertStack";
import { Badge } from "../../components/Badge/Badge";
import { Avatar } from "../../components/Avatar/Avatar";
import { settingsLink, sourceLink } from "./links";
import { ago, formatMinutes } from "./relativeTime";
// The Source tab already decided how a number of rows is printed, and how it
// says «1 guild is» beside «3 guilds are». Rows counted twice on two tabs with
// two different groupings would read as two tables — which is exactly what a
// local `plural` that did NOT group produced here, «1240 guilds are» one alert
// above «1 240 rows».
import { formatCount, plural } from "./roster";
import styles from "./BotsPage.module.css";
import type {
  AwakeGuild,
  BotsOverview,
  OverviewResponse,
  SkippedGuild,
} from "./types";

interface Props {
  projectID: string;
  data?: OverviewResponse;
  loading: boolean;
  enabled: boolean;
}

/**
 * The Overview tab: five numbers, the six layers, and what is being simulated
 * right now.
 *
 * The banners above the numbers are the reason the page exists at all. Without
 * them an operator sees zeros and no explanation — the worker is not running,
 * nothing is awake, the pool is empty, the library is empty. They look
 * identical from the outside and are fixed in different places.
 *
 * So they are checked as a ladder and the FIRST blocking rung is what the page
 * says out loud: the simulation is off → the config is invalid → the sweep has
 * stopped → nothing is awake. The first two are on BotsPage itself, because
 * they hold for every tab. An empty phrase library comes last and is not
 * exclusive: it silences speech, but joining, leaving and helping carry on, so
 * it is shown alongside whatever else is true.
 *
 * The one accusation this page makes — that the worker is down — it has to
 * prove, and the proof is `overdue_bots`: the sweep pushes every bot it claims
 * a lease forward, so a backlog inside an awake guild cannot coexist with a
 * running sweep. An old `last_action_at` proves nothing by itself; a project
 * whose guilds are all asleep is meant to be silent.
 */
export function OverviewTab({ projectID, data, loading, enabled }: Props) {
  if (loading || !data) {
    return <div className={styles.cardSub}>Loading…</div>;
  }
  const o = data.overview;
  const silentFor = minutesSince(o.last_action_at);
  const quietFor = minutesSince(o.last_real_activity_at);
  const freeFor = minutesSince(o.free_since);

  const running = enabled && o.problems.length === 0;
  // Everything about the table is read through this one flag. In `generated`
  // mode all five source numbers are zero, and that is not «nothing is left» —
  // it is «this project has no table», so the screen must not print any of them.
  const fromTable = o.source === "table";
  // The newest way to be stuck, and the only one no other screen can show: the
  // project spends a table and the table has nothing left, so no bot can be
  // created however many guilds ask for one.
  //
  // It sits at the TOP of this tab's ladder and still BELOW the two banners
  // BotsPage draws for the whole page — «simulation is off» and «configuration
  // is invalid». Those two are the more actionable of the three: with either
  // true, a full table would create nothing either, so the switch and the
  // config are what to fix first. `running` is what says so, and it is the same
  // gate every other reason on this tab already sits behind.
  const sourceExhausted = running && fromTable && o.source_exhausted;
  // `source_exhausted` is `left == 0`, and a table that was NEVER FILLED has
  // exactly that. The two are not the same fault: one is a source that ran out,
  // the other is a source nobody has put anything into yet, and telling an
  // operator their empty table is «exhausted» sends them looking for the two
  // hundred rows they believe they lost. `source_total` is what tells them
  // apart.
  const sourceEmpty = sourceExhausted && o.source_total === 0;
  const workerStalled = running && o.awake_guilds > 0 && o.overdue_bots > 0;
  const idle =
    running && !workerStalled && o.seeded_guilds > 0 && o.awake_guilds === 0;
  // Not rungs of the ladder: none of these stops the simulation, and any of
  // them can be true underneath whichever rung is showing. They are gated on
  // `running` all the same — with the config unparsed, nothing is being filled
  // for a reason that has nothing to do with these.
  const poolExhausted = running && o.free === 0 && o.guilds_queued_to_fill > 0;
  const populationAsleep =
    running && o.population_asleep && o.guilds_below_threshold > 0;
  const willNotFill = running && o.skipped_guilds.length > 0;
  const noPhrases = enabled && o.total > 0 && o.phrase_count === 0;

  // Why a free bot is not going anywhere — said only when nothing else could
  // have taken it, because otherwise this guild is not the reason.
  const stranded =
    o.free > 0 && o.guilds_queued_to_fill === 0
      ? freeFor === null
        ? `That is why ${plural(o.free, "bot is", "bots are")} free with nowhere to go.`
        : `That is why ${plural(o.free, "bot has", "bots have")} been free with nowhere to go for ${formatMinutes(freeFor)}.`
      : "";

  return (
    <>
      <AlertStack className={styles.alerts}>
        {sourceExhausted && (
          <Alert
            variant="danger"
            title={`${
              sourceEmpty
                ? "The bot source is a table with no rows in it"
                : "The bot source is exhausted"
            }${
              o.source_guilds_waiting > 0
                ? ` — ${plural(
                    o.source_guilds_waiting,
                    "guild is",
                    "guilds are",
                  )} waiting for members that will never arrive`
                : " — no bot can be created"
            }`}
          >
            {sourceEmpty ? (
              <>Nothing has been imported into it yet</>
            ) : (
              <>
                The table has {formatCount(o.source_left)} unused rows left
                {/* A blocked row looks available on the Source tab and is not:
                    its name belongs to a living player. Left unsaid, the
                    sentence above reads as a contradiction of the table in
                    front of them. */}
                {o.source_blocked > 0 && (
                  <>
                    {" "}
                    ({formatCount(o.source_blocked)} of{" "}
                    {formatCount(o.source_total)}{" "}
                    {o.source_blocked === 1 ? "is" : "are"} blocked by a name a
                    living player already carries)
                  </>
                )}
              </>
            )}
            , and the source is set to <b>From table</b>, so no bot can be
            created.{" "}
            {sourceEmpty ? "Import the rows" : "Import more rows"}, or switch
            the source back to Generated.{" "}
            <Link to={sourceLink(projectID)}>Open Source →</Link>
          </Alert>
        )}
        {workerStalled && (
          <Alert variant="danger" title="The tick worker has stopped sweeping">
            {plural(o.awake_guilds, "guild is", "guilds are")} awake and{" "}
            {o.overdue_bots} of their bots{" "}
            {o.overdue_bots === 1 ? "has" : "have"} been due a check for{" "}
            {formatMinutes(o.overdue_minutes)}. A sweep pushes every bot it
            claims {o.tick_lease_minutes} min forward, so a backlog this old
            means no sweep is running on this deployment. Press{" "}
            <b>Run tick now</b> to sweep once by hand — if the backlog clears,
            the background worker is down and the process needs a restart.
          </Alert>
        )}
        {idle && (
          <Alert
            variant="info"
            title="Nothing is awake — the simulation is idle, not broken"
          >
            {/* «Been in a guild» would be wrong and the page would be caught
                out by its own Simulating-now card: what keeps a guild awake is
                a living player DOING something in it, not sitting in it. */}
            {quietFor === null
              ? "No living player has ever done anything in a guild"
              : `No living player has done anything in a guild for ${formatMinutes(quietFor)}`}
            , and a guild stops being ticked {o.awake_window_minutes} min after
            the last thing one does there.{" "}
            {o.in_guilds === 1
              ? "The one bot in a guild picks up"
              : `All ${o.in_guilds} bots are in place and pick up`}{" "}
            the moment somebody walks in
            {o.actions_24h > 0 && silentFor !== null
              ? ` — the last of their ${o.actions_24h} actions was ${formatMinutes(silentFor)} ago`
              : ""}
            . Set <code>awake_window_minutes</code> to <code>0</code> to
            simulate round the clock.{" "}
            <Link to={settingsLink(projectID, "bots.simulation")}>
              Open Settings →
            </Link>
          </Alert>
        )}
        {poolExhausted && (
          <Alert
            variant="warn"
            title={`${plural(o.guilds_queued_to_fill, "guild is", "guilds are")} waiting to be filled and the pool is empty`}
          >
            All {o.total} bots are placed and no new one could be created —
            most likely the name pool for a locale ran out. Add names on the
            Source tab, or lower <code>target_members</code> in Settings.{" "}
            <Link to={sourceLink(projectID)}>Open Source →</Link>
          </Alert>
        )}
        {populationAsleep && (
          <Alert
            variant="warn"
            title="The project has outgrown the simulation — nothing is being filled"
          >
            {o.active_players} living players did something here in the last 7
            days, which is at or above <code>sleep_when_active_players</code>,
            so the population worker stops for this project entirely and the{" "}
            {plural(o.guilds_below_threshold, "guild", "guilds")} below the
            threshold will stay as they are. The bots already placed carry on.
            Raise <code>sleep_when_active_players</code> to start filling
            again.{" "}
            <Link to={settingsLink(projectID, "bots.maturation")}>
              Open Settings →
            </Link>
          </Alert>
        )}
        {willNotFill && (
          <Alert
            variant="warn"
            title={
              o.guilds_worker_skips === 1
                ? "1 guild is below the threshold and the simulation will never fill it"
                : `${o.guilds_worker_skips} guilds are below the threshold and the simulation will never fill them`
            }
          >
            {o.skipped_guilds.length === 1 ? (
              <>
                <b>{o.skipped_guilds[0].name}</b> —{" "}
                {SKIP_REASON[o.skipped_guilds[0].reason]}. {stranded}{" "}
                {SKIP_FIX[o.skipped_guilds[0].reason]}{" "}
                <Link
                  to={`/projects/${projectID}/guilds/${o.skipped_guilds[0].guild_id}`}
                >
                  Open guild →
                </Link>
              </>
            ) : (
              <>
                <ul>
                  {o.skipped_guilds.map((g) => (
                    <li key={g.guild_id}>
                      <Link to={`/projects/${projectID}/guilds/${g.guild_id}`}>
                        {g.name}
                      </Link>{" "}
                      — {SKIP_REASON[g.reason]}
                    </li>
                  ))}
                  {o.guilds_worker_skips > o.skipped_guilds.length && (
                    <li>
                      …and {o.guilds_worker_skips - o.skipped_guilds.length}{" "}
                      more
                    </li>
                  )}
                </ul>
                {stranded} Fix them where they are, or lower{" "}
                <code>fill_below_members</code> so they stop being counted.
              </>
            )}
          </Alert>
        )}
        {noPhrases && (
          <Alert
            variant="warn"
            title="Bots are silent — no phrases in any locale"
          >
            Joining, leaving and helping still work; every action that needs
            text is skipped.{" "}
            <Link to={`/projects/${projectID}/bots?tab=phrases`}>
              Open Phrases →
            </Link>
          </Alert>
        )}
        {o.warnings.map((w) => (
          <Alert key={w.path} variant="warn">
            {w.message}
          </Alert>
        ))}
      </AlertStack>

      <div className={styles.statGrid}>
        <Stat
          label="Bots total"
          icon={<Bot size={14} />}
          value={String(o.total)}
          sub={o.disabled > 0 ? `${o.disabled} disabled by hand` : "none disabled"}
        />
        <Stat
          label="In guilds"
          icon={<Shield size={14} />}
          value={String(o.in_guilds)}
          sub={inGuildsSub(o, enabled)}
          subTone={
            enabled && o.free > 0 && o.guilds_queued_to_fill === 0
              ? "warn"
              : undefined
          }
        />
        {/* The cap is a headcount per guild, so what stands next to a
            project-wide share is what the cap is DOING, not the cap itself. */}
        <Stat
          label="Bot share"
          icon={<CircleDot size={14} />}
          value={String(o.bot_share_percent)}
          unit="%"
          sub={botShareSub(o)}
        />
        {/* Only in `table` mode. A generated project has no table, and a tile
            reading «0 rows the table can still spend» would be an alarm about
            a thing that does not exist. The grid is auto-fit, so the row is
            five tiles wide without it and six with it — see .statGrid. */}
        {fromTable && (
          <Stat
            label="Source left"
            icon={<Table2 size={14} />}
            value={formatCount(o.source_left)}
            sub="rows the table can still spend"
          />
        )}
        {/* The alarm tone belongs to the one state this page can prove, and
            the age of the last action is shown whether or not there were any
            in the window. */}
        <Stat
          label="Actions · 24h"
          icon={<Zap size={14} />}
          value={String(o.actions_24h)}
          tone={
            workerStalled ? "alarm" : o.actions_24h === 0 ? "muted" : undefined
          }
          sub={actionsSub(o, silentFor, workerStalled)}
          subTone={workerStalled ? "alarm" : undefined}
        />
        <Stat
          label="Seeded guilds"
          icon={<Layers size={14} />}
          value={String(o.seeded_guilds)}
          unit={` / ${o.total_guilds}`}
          sub={seededSub(o)}
          subTone={o.guilds_below_threshold > 0 ? "warn" : undefined}
        />
      </div>

      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h3>Cold-start layers</h3>
          <span className={styles.cardSub}>
            what the platform is doing to make an empty project feel alive
          </span>
        </div>
        {o.total === 0 && !enabled ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyTitle}>No bots yet</div>
            <div className={styles.emptyBody}>
              Turning the simulation on creates the pool and starts filling
              guilds that are below the threshold. Phrases have to exist first —
              without them bots join and help, but never speak.
            </div>
            <div className={styles.emptyActions}>
              <Link to={settingsLink(projectID)}>Open Settings</Link>
              <Link to={`/projects/${projectID}/bots?tab=phrases`}>
                Open Phrases
              </Link>
            </div>
          </div>
        ) : (
          o.layers.map((l) => {
            // The rows come from the server, whole, and this only decorates
            // one of them: the layer that fills guilds is the layer an empty
            // table stops, so its «fill < 8 → up to 16» is replaced by what is
            // actually happening and its link points at the thing to fix. A
            // second, browser-side layer list would be a second answer to
            // «what is the platform doing».
            const stalled =
              l.key === "decor" &&
              sourceExhausted &&
              o.source_guilds_waiting > 0;
            return (
              <div
                key={l.key}
                className={`${styles.layerRow} ${l.on ? styles.layerOn : ""} ${
                  stalled ? styles.layerStalled : ""
                }`}
              >
                <span className={styles.layerNum}>{l.number}</span>
                <div>
                  <div className={styles.layerName}>{l.name}</div>
                  <div className={styles.layerDesc}>{LAYER_BLURB[l.key]}</div>
                </div>
                <div className={styles.layerMetric}>
                  {stalled ? `${o.source_guilds_waiting} stalled` : l.metric}
                </div>
                {stalled ? (
                  <Link className={styles.layerLink} to={sourceLink(projectID)}>
                    Source →
                  </Link>
                ) : (
                  <Link
                    className={styles.layerLink}
                    to={settingsLink(projectID, l.anchor)}
                  >
                    Settings →
                  </Link>
                )}
              </div>
            );
          })
        )}
      </div>

      <div className={`${styles.card} ${enabled ? "" : styles.dim}`}>
        <div className={styles.cardHeader}>
          <h3>Simulating now</h3>
          <span className={styles.cardSub}>
            {enabled
              ? `${data.simulating_now.length} of ${o.seeded_guilds} seeded guilds — the rest are idle and cost nothing`
              : "nothing is being simulated"}
          </span>
        </div>
        {enabled && data.simulating_now.length > 0 && (
          <table className={styles.matrix}>
            <thead>
              <tr>
                <th>Guild</th>
                <th>Real</th>
                <th>Bots</th>
                <th>Last real activity</th>
                <th>Why awake</th>
                <th>Next tick</th>
              </tr>
            </thead>
            <tbody>
              {data.simulating_now.map((g) => {
                const tick = nextTick(g.next_tick_at);
                return (
                <tr key={g.guild_id}>
                  <td>
                    <div className={styles.who}>
                      <Avatar name={g.name} size={26} />
                      <div>
                        <div className={styles.whoName}>
                          <Link to={`/projects/${projectID}/guilds/${g.guild_id}`}>
                            {g.name}
                          </Link>
                        </div>
                        <div className={styles.whoSub}>
                          {g.country ?? "—"} · {g.members}/{g.max_members}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className={styles.mono}>{g.real_members}</td>
                  <td className={styles.mono}>{g.bots}</td>
                  <td className={styles.mono}>{ago(g.last_real_activity_at)}</td>
                  <td>
                    <Badge variant={whyVariant(g)} dot>
                      {g.why_awake}
                    </Badge>
                  </td>
                  <td
                    className={`${styles.mono} ${tick.overdue ? styles.overdue : ""}`}
                  >
                    {tick.text}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        )}
        <div className={styles.cardFoot}>
          <span>
            {enabled
              ? "A guild goes idle once no real player has acted inside the awake window, and stops being ticked entirely."
              : "Turn the simulation on to see live guilds here."}
          </span>
          <span className={styles.mono}>
            config: bots.simulation.awake_window_minutes
          </span>
        </div>
      </div>
    </>
  );
}

/** Why the population worker will not fill a guild — one phrase per test it
 *  applies (guildFillFilter and guildNotMatured in repository.go), in the
 *  order the page reports them. */
const SKIP_REASON: Record<SkippedGuild["reason"], string> = {
  not_open: "not open, and bots only join open guilds",
  bots_disabled: "opted out of bots, so nothing will ever be added to it",
  matured: "has enough living members of its own, so its bots are leaving",
  kick_pause: "paused, because a bot was kicked from it recently",
  bot_cap: "already holding as many bots as the cap allows",
  // Not «at the target size»: a guild only reaches this reason while it is
  // below fill_below_members, and a valid config keeps that below
  // target_members (config.go rejects the other way round). So the ceiling it
  // has hit is always its own.
  no_room: "full — it has hit its own member cap",
};

/** …and the handle that lifts each one. Every reason ends the same way,
 *  because a guild that should not be counted at all is always a threshold
 *  away from not being counted. */
const SKIP_FIX: Record<SkippedGuild["reason"], ReactNode> = {
  not_open: (
    <>
      Open the guild, or lower <code>fill_below_members</code> so it stops being
      counted.
    </>
  ),
  bots_disabled: (
    <>
      Clear <code>bots_disabled</code> on the guild, or lower{" "}
      <code>fill_below_members</code> so it stops being counted.
    </>
  ),
  matured: (
    <>
      Nothing to fix unless this is early — raise{" "}
      <code>leave_when_real_members</code> to keep decorating it, or lower{" "}
      <code>fill_below_members</code> so it stops being counted.
    </>
  ),
  kick_pause: (
    <>
      Wait for the pause to run out, or lower{" "}
      <code>kick_refill_pause_hours</code>.
    </>
  ),
  bot_cap: (
    <>
      Raise <code>max_bot_share</code>, or lower <code>fill_below_members</code>{" "}
      so it stops being counted.
    </>
  ),
  no_room: (
    <>
      Raise the guild&apos;s own member cap, or lower{" "}
      <code>fill_below_members</code> so it stops being counted.
    </>
  ),
};

/** A free bot is only waiting if there is somewhere for it to go. */
function inGuildsSub(o: BotsOverview, enabled: boolean): string {
  if (!enabled) return "frozen in place";
  if (o.free === 0) return "all placed";
  if (o.guilds_queued_to_fill === 0) {
    return `${o.free} free · nowhere to place ${o.free === 1 ? "it" : "them"}`;
  }
  return `${o.free} free, waiting to be placed`;
}

function botShareSub(o: BotsOverview): string {
  if (o.bot_cap_per_guild <= 0) return "of all guild members";
  const at =
    o.guilds_at_bot_cap === 0
      ? "none"
      : plural(o.guilds_at_bot_cap, "guild", "guilds");
  // A spread project has no single cap to name, so the tile names the span
  // instead of one guild's ceiling standing in for twenty different ones.
  const cap =
    o.bot_cap_per_guild_min < o.bot_cap_per_guild
      ? `a cap of ${o.bot_cap_per_guild_min} to ${o.bot_cap_per_guild} bots`
      : `the ${o.bot_cap_per_guild}-bot cap`;
  return `of all guild members · ${at} at ${cap}`;
}

function actionsSub(
  o: BotsOverview,
  silentFor: number | null,
  stalled: boolean,
): string {
  const head =
    o.actions_24h > 0
      ? `${o.actions_for_players_24h} answered a real player`
      : silentFor === null
        ? "nothing yet"
        : "nothing in 24 h";
  const tail = stalled
    ? `${plural(o.overdue_bots, "bot", "bots")} overdue`
    : silentFor === null
      ? null
      : `last one ${formatMinutes(silentFor)} ago`;
  return tail ? `${head} · ${tail}` : head;
}

/** Two numbers rather than one, because they are two different facts: what the
 *  worker is about to do, and what it will never do. */
function seededSub(o: BotsOverview): string {
  if (o.guilds_below_threshold === 0) return "none need help";
  if (o.guilds_worker_skips === 0) {
    return `${o.guilds_queued_to_fill} queued to fill`;
  }
  return `${o.guilds_queued_to_fill} queued to fill · ${o.guilds_worker_skips} the worker will not touch`;
}

/** The prose under each layer's name. Kept next to the page rather than in the
 *  API: it explains the design, and the design does not change per project. */
const LAYER_BLURB: Record<string, string> = {
  concentration:
    "Recommendations steer newcomers into the fullest guild that still has room, instead of spreading them one per guild.",
  decor:
    "Guilds below the threshold are topped up with bots so search and recommendations are never empty.",
  backfill:
    "On a real player's first visit the guild chat is written back-dated, so history exists instead of a blank screen.",
  response:
    "A real player's help request is always answered — first donation in 30–120 s, then the rest of the queue.",
  newcomer:
    "For the first hour after joining a player gets a welcome, faster replies and faster help. Then it decays to the normal background.",
  maturation:
    "Bots quietly leave guilds that have enough real members, and the whole system sleeps once the project outgrows it.",
};

function whyVariant(g: AwakeGuild): "accent" | "info" | "warn" | "neutral" {
  switch (g.why_awake) {
    case "backfill pending":
      return "warn";
    case "newcomer hour":
      return "accent";
    case "real activity":
      return "neutral";
    default:
      return "info";
  }
}

function Stat({
  label,
  icon,
  value,
  unit,
  sub,
  tone,
  subTone,
}: {
  label: string;
  icon: ReactNode;
  value: string;
  unit?: string;
  sub: string;
  tone?: "muted" | "alarm";
  subTone?: "warn" | "alarm";
}) {
  return (
    <div className={styles.statCard}>
      <div className={styles.statHead}>
        <span className={styles.statLabel}>{label}</span>
        <span
          className={`${styles.statIcon} ${tone === "alarm" ? styles.statIconAlarm : ""}`}
          aria-hidden="true"
        >
          {icon}
        </span>
      </div>
      <div
        className={`${styles.statValue} ${
          tone === "muted"
            ? styles.statValueMuted
            : tone === "alarm"
              ? styles.statValueAlarm
              : ""
        }`}
      >
        {value}
        {unit && <span className={styles.statUnit}>{unit}</span>}
      </div>
      <div
        className={`${styles.statSub} ${
          subTone === "warn"
            ? styles.statSubWarn
            : subTone === "alarm"
              ? styles.statSubAlarm
              : ""
        }`}
      >
        {sub}
      </div>
    </div>
  );
}

function minutesSince(iso: string | null): number | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  return Math.max(0, Math.floor((Date.now() - then) / 60_000));
}

/**
 * The «Next tick» cell. A check due a few seconds ago is «now» — the sweep runs
 * every few seconds and is about to take it. One due long ago is not: printing
 * «now» under a banner that says nothing has been swept for 47 minutes is the
 * page arguing with itself, which is the whole thing this page is for. Overdue
 * is the same number the banner is built on, per row (bots-diagnosis.html
 * L569-L571).
 */
function nextTick(iso: string | null): { text: string; overdue: boolean } {
  if (!iso) return { text: "—", overdue: false };
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return { text: "—", overdue: false };
  const s = Math.floor((then - Date.now()) / 1000);
  if (s <= 0) {
    const m = Math.floor(-s / 60);
    return m < 1
      ? { text: "now", overdue: false }
      : { text: `${formatMinutes(m)} overdue`, overdue: true };
  }
  if (s < 60) return { text: `in ${s}s`, overdue: false };
  const m = Math.floor(s / 60);
  return { text: `in ${m}m ${s % 60}s`, overdue: false };
}
